import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type FTPUser, type Client, type Node } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, ErrorBox, Badge } from '../components/ui'
import { IconPlus } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

export function FTPPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [pwUser, setPwUser] = useState<FTPUser | null>(null)
  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: users = [] } = useQuery({ queryKey: ['ftp-users'], queryFn: async () => (await api.get<FTPUser[]>('/ftp-users')).data })
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: async () => (await api.get<Client[]>('/clients')).data })
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]))
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  const [fNode, setFNode] = useState('')
  const [fSearch, setFSearch] = useState('')
  const filtered = useMemo(() => {
    let list = users
    if (fNode) list = list.filter(u => u.node_id === fNode)
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(u => u.name.toLowerCase().includes(q))
    }
    return list
  }, [users, fNode, fSearch])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/ftp-users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ftp-users'] }),
  })
  const setHomeSub = useMutation({
    mutationFn: async ({ id, home_sub }: { id: number; home_sub: string }) =>
      (await api.post(`/ftp-users/${id}/home-sub`, { home_sub })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ftp-users'] }),
  })

  return (
    <>
      <PageHeader
        title="FTP / SFTP"
        subtitle="SFTP-пользователи с chroot в /var/www/<клиент>. Подключение через ssh://порт 22."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить FTP-пользователя</Button>}
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Логин…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
      </FilterBar>
      <Table<FTPUser>
        rows={filtered} empty={users.length === 0 ? 'Нет FTP-пользователей.' : 'Ничего не найдено.'}
        cols={[
          { key: 'n', label: 'Логин', render: (u) => <span className="font-mono font-medium">{u.name}</span> },
          { key: 'nd', label: 'Нода', render: (u) => <span className="text-[13px] text-neutral-700">{nodeMap[u.node_id]?.name || '—'}</span> },
          { key: 'c', label: 'Клиент', render: (u) => <span className="font-mono text-[12px]">{clientMap[u.client_id]?.unix_user || u.client_id}</span> },
          { key: 'h', label: 'Домашняя папка', render: (u) => <span className="font-mono text-[12px] text-neutral-600">{u.home_dir}</span> },
          { key: 'st', label: 'Статус', render: (u) => u.is_active ? <Badge tone="green">активен</Badge> : <Badge>отключён</Badge> },
          { key: 'act', label: '', w: '1%', render: (u) => (
            <div className="flex justify-end">
              <RowMenu items={[
                { label: 'Сменить пароль', onClick: () => setPwUser(u) },
                { label: 'Сменить рабочую папку', onClick: () => {
                  const sub = prompt(`Новая подпапка в /var/www/${clientMap[u.client_id]?.unix_user || 'клиент'}?\n(пусто = корень)`, '')
                  if (sub === null) return
                  setHomeSub.mutate({ id: u.id, home_sub: sub.trim() })
                }},
                { divider: true, label: '', onClick: () => {} },
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить ${u.name}?`) && del.mutate(u.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {addOpen && <AddFTPModal clients={clients} onClose={() => setAddOpen(false)}
        onTask={(t) => { setAddOpen(false); setTaskId(t); qc.invalidateQueries({ queryKey: ['ftp-users'] }) }} />}
      {pwUser && <PwModal user={pwUser} onClose={() => setPwUser(null)} onTask={(t) => { setPwUser(null); setTaskId(t) }} />}
      {taskId && <TaskProgress taskId={taskId} onClose={() => setTaskId(null)} />}
    </>
  )
}

function AddFTPModal({ clients, onClose, onTask }: { clients: Client[]; onClose: () => void; onTask: (id: string) => void }) {
  const [f, setF] = useState({ client_id: clients[0]?.id || 0, name: '', password: '', home_sub: 'data' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/ftp-users', f)).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить FTP-пользователя">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Клиент">
          <Select value={f.client_id} onChange={(e) => setF({ ...f, client_id: +e.target.value })}>
            {clients.map(c => <option key={c.id} value={c.id}>{c.unix_user}</option>)}
          </Select>
        </Field>
        <Field label="Логин"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="myftp" required autoFocus /></Field>
        <Field label="Пароль"><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required /></Field>
        <Field label="Рабочая папка" hint="Относительно /var/www/<клиент>"><Input value={f.home_sub} onChange={(e) => setF({ ...f, home_sub: e.target.value })} /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>Создать</Button>
        </div>
      </form>
    </Modal>
  )
}

function PwModal({ user, onClose, onTask }: { user: FTPUser; onClose: () => void; onTask: (id: string) => void }) {
  const [pw, setPw] = useState('')
  const [err, setErr] = useState('')
  const submit = useMutation({
    mutationFn: async () => (await api.post(`/ftp-users/${user.id}/password`, { password: pw })).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title={`Смена пароля: ${user.name}`}>
      <form onSubmit={(e) => { e.preventDefault(); submit.mutate() }} className="space-y-4">
        <Field label="Новый пароль"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={submit.isPending}>Сохранить</Button>
        </div>
      </form>
    </Modal>
  )
}
